import { useState } from 'react';
import { AlertTriangle, Check, ChevronRight, FileText, Printer, X } from 'lucide-react';
import Sheet from './Sheet';
import { api, errorMessage } from '../lib/api';
import { clearConfirmSent, loadConfirmSent, saveConfirmSent } from '../lib/store';
import { adoptReceipt } from '../lib/sync';
import { KINDS, liveDest, unitVerdict } from '../lib/verdict';
import { looseBoxes } from '../lib/boxes';
import { cn, qty } from '../lib/utils';

/* Review what was counted, then push it to Odoo: one receipt per purchase
   order, in one go. Missing quantities always stay open on a backorder: the
   choice to drop them was removed after a misclick cancelled the rest of four
   orders (closing an order short is purchasing's call, in Odoo). Odoo refuses a receipt of products
   whose packaging is not confirmed, or whose boxes carry no supplier barcode
   while "No EAN from vendor" is off: those get a last check before anything
   is sent, item by item (a one-tap fix with `onFlags`, the line with `onFix`,
   or left for the backorder with `onLeave`). */

/** What Odoo would refuse on a counted line: 'packaging', 'barcode'. */
function odooNeeds(line) {
  const out = [];
  if (line.packaging_status !== 'confirmed') out.push('packaging');
  if (line.boxes?.length && !line.no_vendor_ean && !line.boxes.some((b) => b.vendor_barcode)) out.push('barcode');
  return out;
}

export default function ConfirmSheet({ combined, counts, boxes = {}, dest, scans, onClose, onDone, onValidated, onFix, onScanBox, onFlags, onLeave, onRefresh }) {
  const [busy, setBusy] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [reprint, setReprint] = useState({}); // receipt id -> label print outcome after "Print labels again"
  const [checking, setChecking] = useState(false); // the last check before Odoo is on screen
  const [fixing, setFixing] = useState({}); // line id -> {busy, error}

  const lines = combined.lines.filter((l) => l.status === 'open');
  const rows = lines.map((l) => {
    const n = Number(counts[l.id] || 0);
    const status = n === 0 ? 'none' : n < l.to_receive ? 'short' : n > l.to_receive ? 'over' : 'ok';
    const d = liveDest(dest?.lines?.[l.id] || null, counts, { final: true }); // the truck is done: HOLD resolves to OUT or CROSSDOCK
    const hold = d?.orders?.find((o) => o.kind === 'crossdock' && o.pending_delivery);
    const released = d?.orders?.find((o) => o.kind === 'out' && o.arrived_with?.length);
    return { line: l, n, status, loose: looseBoxes(l, boxes[l.id] || {}, n), dest: d, hold, released };
  });
  const setAside = rows.filter((r) => r.n > 0 && (r.hold || r.released)); // what the operator parked during the session
  const counted = rows.filter((r) => r.n > 0);
  const blocked = counted.map((r) => ({ ...r, needs: odooNeeds(r.line) })).filter((r) => r.needs.length);
  const missing = rows.filter((r) => r.status === 'none' || r.status === 'short');
  const units = counted.reduce((s, r) => s + r.n, 0);
  const orders = combined.orders.filter((o) => counted.some((r) => r.line.po.id === o.id));

  const submit = async () => {
    setBusy(true);
    setError(null);
    const validated = [];
    const failed = [];
    const errors = [];
    const reports = []; // one PDF per purchase order, whatever its outcome
    const labels = []; // product labels sent to the operator's printer, per order
    const kept = {}; // po id -> what stays counted after a receipt booked in part
    const stale = []; // lines Odoo has booked since they were counted: their counts go back to zero
    let refused = 0; // orders Inny stopped before Odoo: this device's lines were out of date
    let receiptUnits = 0;
    try {
      for (const o of orders) {
        setProgressText(`Booking ${o.name}…`);
        const mine = counted.filter((r) => r.line.po.id === o.id);
        const sent = loadConfirmSent(o.id);
        const open = Object.fromEntries(mine.map((r) => [r.line.id, sent[r.line.id] ?? r.line.to_receive]));
        const body = {
          backorder: true,
          lines: mine.map((r) => ({ line_id: r.line.id, qty: r.n, expected: open[r.line.id], destination: r.dest ? unitVerdict(r.dest, 1) : '' })),
          // an unknown code has no order: it goes with the first receipt only
          scans: (scans || []).filter((s) => (s.line_id ? mine.some((r) => r.line.id === s.line_id) : o === orders[0])).slice(-500),
        };
        saveConfirmSent(o.id, open);
        try {
          const res = await api.confirm(o.id, body);
          clearConfirmSent(o.id);
          if (res.kept) kept[o.id] = res.kept;
          if (res.report_url) reports.push({ po: o.name, url: res.report_url, inOdoo: res.report_in_odoo });
          if (res.labels && res.labels.status !== 'off' && res.labels.status !== 'none') labels.push({ po: o.name, receipt_id: res.receipt_id, ...res.labels });
          validated.push(...(res.validated || []).map((v) => ({ ...v, po: o.name })));
          failed.push(...(res.failed || []).map((f) => ({ ...f, po: o.name })));
          errors.push(...(res.errors || []));
          receiptUnits += Number(res.units || 0);
        } catch (err) {
          const data = err?.response?.data;
          // no answer, or Odoo stopped halfway: it may have booked, so the retry keeps these numbers
          if (err?.response && (err.response.status < 500 || data?.status)) clearConfirmSent(o.id);
          if (data?.stale?.length) stale.push(...data.stale);
          if (data?.status) {
            if (data.report_url) reports.push({ po: o.name, url: data.report_url, inOdoo: data.report_in_odoo });
            if (data.labels && data.labels.status !== 'off' && data.labels.status !== 'none') labels.push({ po: o.name, receipt_id: data.receipt_id, ...data.labels });
            validated.push(...(data.validated || []).map((v) => ({ ...v, po: o.name })));
            failed.push(...(data.failed || []).map((f) => ({ ...f, po: o.name })));
            errors.push(...(data.errors || []));
            receiptUnits += Number(data.units || 0);
          } else {
            if (data?.unconfirmed?.length) refused += 1;
            failed.push({ id: o.id, name: o.name, po: o.name, error: errorMessage(err) });
          }
        }
      }
      if (stale.length) {
        stale.forEach((s) => {
          const line = combined.lines.find((l) => l.id === s.line_id);
          if (line) onLeave?.(line);
        });
        await onRefresh?.();
      }
      if (refused && refused === failed.length && !validated.length) {
        // nothing went to Odoo: fresh lines, and the last check shows what to fix
        await onRefresh?.();
        setChecking(true);
        return;
      }
      const status = failed.length === 0 ? 'validated' : validated.length ? 'partial' : 'failed';
      setResult({ status, validated, failed, errors, units: receiptUnits, reports, labels, already: stale.length > 0 && !validated.length, names: orders.map((o) => o.name) }); // the counts are cleared next
      // the counts are in Odoo, whatever button (or back gesture) comes next
      const done = orders.filter((o) => validated.some((v) => v.po === o.name) && !kept[o.id]).map((o) => o.id);
      Object.entries(kept).forEach(([id, state]) => adoptReceipt(Number(id), state));
      if (done.length || Object.keys(kept).length) onValidated?.(done);
      if (Object.keys(kept).length) await onRefresh?.(); // the retry needs what is open now
    } finally {
      setBusy(false);
      setProgressText('');
    }
  };

  const printAgain = async (l) => {
    setReprint((r) => ({ ...r, [l.receipt_id]: { busy: true } }));
    try {
      const res = await api.printLabels(l.receipt_id);
      setReprint((r) => ({ ...r, [l.receipt_id]: res }));
    } catch (err) {
      setReprint((r) => ({ ...r, [l.receipt_id]: { status: 'failed', message: errorMessage(err) } }));
    }
  };

  if (result) {
    const ok = result.status === 'validated';
    const backorders = result.validated.flatMap((v) => v.backorders || []);
    return (
      <Sheet open dismissible={false} label="Receipt result">
        <div className="px-4 pb-4 pt-3">
          <div className={cn('flex items-center gap-3 rounded-2xl px-4 py-4', ok ? 'tone-out bg-tone-bg' : 'tone-over bg-tone-bg')}>
            {ok ? <Check className="pop h-8 w-8 text-tone" /> : <AlertTriangle className="pop h-8 w-8 text-tone" />}
            <div>
              <p className="font-cond text-[26px] font-bold leading-none text-tone">{ok ? 'Received in Odoo' : result.status === 'partial' ? 'Partly received' : result.already ? 'Already in Odoo' : 'Not received'}</p>
              <p className="mt-1 text-[14px] text-ink2">
                {qty(result.units)} {result.units === 1 ? 'unit' : 'units'} on {result.names.join(', ')}
              </p>
            </div>
          </div>
          {result.validated.length > 0 && (
            <p className="mt-3 text-[14px] text-ink2">
              Validated {result.validated.map((v) => v.name).join(', ')}.
              {backorders.length > 0 && (
                <>
                  {' '}The rest stays open on <span className="font-semibold text-ink">{backorders.join(', ')}</span>: confirm it when the next pallet arrives.
                </>
              )}
            </p>
          )}
          {result.failed.length > 0 && (
            <ul className="mt-3 space-y-1 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">
              {result.failed.map((f, i) => (
                <li key={`${f.id}-${i}`}>
                  {f.name}: {f.error}
                </li>
              ))}
            </ul>
          )}
          {result.errors.length > 0 && <p className="mt-2 text-[13px] text-ink2">{result.errors.join(' · ')}</p>}
          {result.labels.map((l0) => {
            const l = reprint[l0.receipt_id] ? { ...l0, ...reprint[l0.receipt_id] } : l0;
            const sent = l.status === 'sent';
            return (
              <div key={l0.receipt_id} className={cn('mt-3 flex items-start gap-2 rounded-xl px-3 py-2 text-[14px]', sent ? 'bg-sunken text-ink2' : 'bg-alert-bg text-alert')}>
                <Printer className="mt-0.5 h-4 w-4 shrink-0" />
                <span className="min-w-0 flex-1">
                  {l.busy ? (
                    'Sending the labels again…'
                  ) : sent ? (
                    <>
                      {qty(l.units)} product {l.units === 1 ? 'label' : 'labels'}{result.labels.length > 1 ? ` for ${l.po}` : ''} sent to <span className="font-semibold text-ink">{l.printer || 'your label printer'}</span>.
                    </>
                  ) : (
                    <>
                      Labels not printed{result.labels.length > 1 ? ` for ${l.po}` : ''}: {l.message}
                    </>
                  )}
                  {!l.busy && (
                    <button type="button" onClick={() => printAgain(l0)} className="ml-2 inline-flex items-center gap-1 font-semibold underline-offset-2 hover:underline">
                      Print {sent ? 'again' : 'labels'}
                    </button>
                  )}
                </span>
              </div>
            );
          })}
          {result.reports.length > 0 && (
            <div className="mt-3">
              <div className="flex flex-wrap gap-2">
                {result.reports.map((r) => (
                  <a key={r.url} href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-sunken px-3 py-2.5 text-[14px] font-semibold active:bg-line">
                    <FileText className="h-4 w-4" /> {result.reports.length > 1 ? `Report ${r.po}` : 'Receipt report'} (PDF)
                  </a>
                ))}
              </div>
              <p className="mt-1.5 text-[12px] text-ink2">
                {result.reports.every((r) => r.inOdoo?.status === 'attached')
                  ? `Attached to ${result.reports.length > 1 ? 'each purchase order' : 'the purchase order'} in Odoo, and listed under Receipts and reports in the menu.`
                  : result.reports.some((r) => r.inOdoo?.status === 'failed')
                    ? `Listed under Receipts and reports in the menu. Could not attach it in Odoo: ${result.reports.find((r) => r.inOdoo?.status === 'failed').inOdoo.message}`
                    : 'Find it again any time under Receipts and reports in the menu.'}
              </p>
            </div>
          )}
          {!ok && counted.length > 0 ? (
            <>
              <button type="button" onClick={() => setResult(null)} className="mt-5 w-full rounded-xl bg-ink py-3.5 text-[16px] font-semibold text-bg active:opacity-80">
                Try again
              </button>
              <button type="button" onClick={() => onDone?.(result, 'list')} className="mt-2 w-full rounded-xl bg-sunken py-3.5 text-[16px] font-semibold active:bg-line">
                Back to orders
              </button>
            </>
          ) : backorders.length > 0 ? (
            <>
              <button type="button" onClick={() => onDone?.(result, 'stay')} className="mt-5 w-full rounded-xl bg-ink py-3.5 text-[16px] font-semibold text-bg active:opacity-80">
                Continue on {combined.multi ? 'these orders' : 'this order'}
              </button>
              <button type="button" onClick={() => onDone?.(result, 'list')} className="mt-2 w-full rounded-xl bg-sunken py-3.5 text-[16px] font-semibold active:bg-line">
                Back to orders
              </button>
            </>
          ) : (
            <button type="button" onClick={() => onDone?.(result, 'list')} className="mt-5 w-full rounded-xl bg-ink py-3.5 text-[16px] font-semibold text-bg active:opacity-80">
              Back to orders
            </button>
          )}
        </div>
      </Sheet>
    );
  }

  const fix = async (line, flags) => {
    setFixing((f) => ({ ...f, [line.id]: { busy: true } }));
    try {
      await onFlags?.(line, flags);
      setFixing((f) => ({ ...f, [line.id]: null }));
    } catch (err) {
      setFixing((f) => ({ ...f, [line.id]: { error: errorMessage(err) } }));
    }
  };

  if (checking) {
    const go = () => {
      setChecking(false);
      submit();
    };
    return (
      <Sheet open onClose={() => setChecking(false)} label="Last check before Odoo">
        <div className="px-4 pb-4 pt-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="font-cond text-[24px] font-bold leading-none">{blocked.length ? 'Last check before Odoo' : 'All set for Odoo'}</p>
              <p className="mt-1 text-[14px] text-ink2">
                {blocked.length
                  ? `Odoo refuses the receipt while ${blocked.length === 1 ? 'this item misses' : `these ${blocked.length} items miss`} something. Fix it here, or leave it for the next receipt.`
                  : 'Everything Odoo asks for is there.'}
              </p>
            </div>
            <button type="button" onClick={() => setChecking(false)} aria-label="Back" className="-mr-2 -mt-1 flex h-10 w-10 items-center justify-center rounded-full active:bg-sunken">
              <X className="h-5 w-5" />
            </button>
          </div>
          <ul className="mt-4 max-h-[55vh] space-y-2 overflow-y-auto">
            {blocked.map(({ line, n, needs }) => {
              const st = fixing[line.id] || {};
              return (
                <li key={line.id} className="rounded-xl border border-line px-3 py-2.5">
                  <div className="flex items-start justify-between gap-2">
                    <span className="min-w-0">
                      <span className="clamp-2 block text-[14px] font-semibold leading-tight">{line.name}</span>
                      {combined.multi && <span className="font-cond block text-[12px] font-bold text-ink2">{line.po.name}</span>}
                    </span>
                    <span className="font-cond shrink-0 text-[20px] font-bold leading-none">{qty(n)}</span>
                  </div>
                  {needs.includes('packaging') && (
                    <div className="mt-2">
                      <p className="flex items-center gap-1.5 text-[13px] font-semibold text-alert">
                        <AlertTriangle className="h-4 w-4" /> {line.packaging_status === 'none' ? 'No box on file' : 'Packaging not confirmed'}
                      </p>
                      {line.packaging_status === 'unconfirmed' && line.packaging_complete ? (
                        <button type="button" disabled={st.busy} onClick={() => fix(line, { box_confirmed: true })} className="mt-1.5 w-full rounded-lg bg-ink py-2.5 text-[14px] font-semibold text-bg active:opacity-80 disabled:opacity-40">
                          Confirm the packaging
                        </button>
                      ) : (
                        <button type="button" onClick={() => onFix?.(line)} className="mt-1.5 flex w-full items-center justify-center gap-1 rounded-lg bg-ink py-2.5 text-[14px] font-semibold text-bg active:opacity-80">
                          Add the size and weight <ChevronRight className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                  {needs.includes('barcode') && (
                    <div className="mt-2">
                      <p className="flex items-center gap-1.5 text-[13px] font-semibold text-alert">
                        <AlertTriangle className="h-4 w-4" /> No supplier barcode on file
                      </p>
                      <div className="mt-1.5 grid grid-cols-2 gap-2">
                        <button type="button" disabled={st.busy} onClick={() => fix(line, { no_ean_from_vendor: true })} className="rounded-lg bg-ink py-2.5 text-[14px] font-semibold text-bg active:opacity-80 disabled:opacity-40">
                          Box has no barcode
                        </button>
                        <button type="button" onClick={() => onScanBox?.(line)} className="flex items-center justify-center gap-1 rounded-lg bg-sunken py-2.5 text-[14px] font-semibold active:bg-line">
                          Scan it <ChevronRight className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  {st.error && <p className="mt-1.5 text-[13px] text-alert">{st.error}</p>}
                  <button type="button" disabled={st.busy} onClick={() => onLeave?.(line)} className="mt-1 w-full py-2.5 text-center text-[13px] font-semibold text-ink2 underline-offset-2 active:underline disabled:opacity-40">
                    {st.busy ? 'Saving in Odoo…' : 'Leave it for the next receipt'}
                  </button>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            onClick={go}
            disabled={blocked.length > 0 || counted.length === 0}
            className="mt-4 w-full rounded-xl bg-ink py-3.5 text-[16px] font-semibold text-bg active:opacity-80 disabled:opacity-40"
          >
            {counted.length === 0 ? 'Nothing counted' : blocked.length ? `${blocked.length} to check` : orders.length > 1 ? `Confirm ${orders.length} receipts in Odoo` : 'Confirm in Odoo'}
          </button>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet open onClose={busy ? undefined : onClose} dismissible={!busy} label="Confirm receipt">
      <div className="px-4 pb-4 pt-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-cond text-[24px] font-bold leading-none">Confirm receipt</p>
            <p className="mt-1 text-[14px] text-ink2">
              {orders.map((o) => o.name).join(', ') || combined.name} · {qty(units)} {units === 1 ? 'unit' : 'units'} on {counted.length} of {lines.length} lines
            </p>
          </div>
          <button type="button" onClick={onClose} disabled={busy} aria-label="Close" className="-mr-2 -mt-1 flex h-10 w-10 items-center justify-center rounded-full active:bg-sunken">
            <X className="h-5 w-5" />
          </button>
        </div>

        <ul className="mt-4 max-h-[40vh] divide-y divide-line overflow-y-auto rounded-xl border border-line">
          {rows.map(({ line, n, status, loose, dest: d, hold }) => (
            <li key={line.id} className="flex items-center gap-3 px-3 py-2">
              <span className="min-w-0 flex-1">
                <span className="clamp-2 block text-[14px] font-semibold leading-tight">{line.name}</span>
                {combined.multi && <span className="font-cond mr-2 text-[13px] font-bold text-ink2">{line.po.name}</span>}
                {n > 0 && d?.primary && (
                  <span className={cn(KINDS[d.primary].tone, 'mr-2 inline-flex items-center gap-1 rounded-md bg-tone-bg px-1.5 py-0.5 text-[11px] font-bold text-tone')}>
                    {KINDS[d.primary].word}
                  </span>
                )}
                {hold && <span className="block text-[12px] text-hold">hold: {hold.waiting_on.join(', ')} did not arrive</span>}
                {status === 'short' && <span className="text-[12px] text-hold">{qty(line.to_receive - n)} missing</span>}
                {status === 'none' && (
                  <span className={cn('text-[12px]', line.shipped_pending ? 'font-semibold text-hold' : 'text-ink2')}>
                    {line.shipped_pending ? `supplier shipped ${qty(line.shipped)}, not scanned` : 'not scanned'}
                  </span>
                )}
                {status === 'over' && <span className="text-[12px] text-alert">{qty(n - line.to_receive)} extra</span>}
                {n > 0 && odooNeeds(line).length > 0 && (
                  <span className="block text-[12px] font-semibold text-alert">{odooNeeds(line).includes('packaging') ? 'packaging not confirmed' : 'no supplier barcode'}</span>
                )}
                {loose > 0 && <span className="block text-[12px] text-hold">{qty(loose)} loose {loose === 1 ? 'carton' : 'cartons'} without a full set, not counted</span>}
              </span>
              <span className={cn('font-cond text-[22px] font-bold leading-none', status === 'ok' && 'text-out', status === 'over' && 'text-alert', status === 'none' && 'text-ink2')}>
                {qty(n)}
                <span className="text-[15px] font-semibold text-ink2"> / {qty(line.to_receive)}</span>
              </span>
            </li>
          ))}
        </ul>

        {setAside.length > 0 && (
          <section className="mt-3 rounded-xl bg-sunken px-3 py-2">
            <h3 className="text-[13px] font-semibold">Set aside during scanning: where it goes now</h3>
            <ul className="mt-1 space-y-1">
              {setAside.map((r) => (
                <li key={r.line.id} className="flex items-start gap-2 text-[13px]">
                  <span className={cn(KINDS[r.hold ? 'crossdock' : 'out'].tone, 'mt-0.5 inline-flex shrink-0 rounded-md bg-tone-bg px-1.5 py-0.5 text-[11px] font-bold text-tone')}>
                    {KINDS[r.hold ? 'crossdock' : 'out'].word}
                  </span>
                  <span className="min-w-0">
                    <span className="clamp-2 block font-semibold leading-tight">{r.line.name}</span>
                    <span className="block text-ink2">{r.hold ? `${r.hold.waiting_on.join(', ')} did not arrive` : `${r.released.arrived_with.join(', ')} arrived too`}</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}
        {missing.length > 0 && (
          <p className="mt-4 rounded-xl bg-sunken px-3 py-3 text-[14px]">
            <span className="font-semibold">The missing items stay open.</span>{' '}
            <span className="text-ink2">Odoo creates a backorder for what is still to come, so you can confirm the next pallet later.</span>
          </p>
        )}

        {error && <p className="mt-3 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{error}</p>}

        <button
          type="button"
          onClick={blocked.length ? () => setChecking(true) : submit}
          disabled={busy || counted.length === 0}
          className="mt-4 w-full rounded-xl bg-ink py-3.5 text-[16px] font-semibold text-bg active:opacity-80 disabled:opacity-40"
        >
          {busy ? progressText || 'Sending to Odoo…' : blocked.length > 0 ? 'Confirm in Odoo: last check first' : orders.length > 1 ? `Confirm ${orders.length} receipts in Odoo` : 'Confirm in Odoo'}
        </button>
      </div>
    </Sheet>
  );
}
